import React, { useState, useEffect } from 'react';
import { useProject } from '../contexts/ProjectContext';
import { AlertCircle, CheckCircle2, XCircle, FileText, Loader2, PlayCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';

interface DetectedChange {
  id: number;
  change_type: 'ADD' | 'MODIFY' | 'DELETE' | 'MERGE' | 'SPLIT' | 'REORDER';
  target_step_number: string | null;
  target_location: string;
  current_content?: string;
  proposed_content: string;
  reasoning: string;
  confidence_score: number;
  section_name?: string;
  requires_override: boolean;
}

interface ImpactSummary {
  total_changes: number;
  add_count: number;
  modify_count: number;
  delete_count: number;
  affected_steps: number;
  preserved_steps: number;
  overall_confidence: number;
  high_confidence_changes: number;
}

const ChangeDetection: React.FC = () => {
  const { currentProject } = useProject();
  const [sourceType, setSourceType] = useState<'transcript' | 'notes' | 'document' | 'manual'>('notes');
  const [sourceReference, setSourceReference] = useState('');
  const [sourceContent, setSourceContent] = useState('');
  const [processMaps, setProcessMaps] = useState<any[]>([]);
  const [selectedMapId, setSelectedMapId] = useState<number | null>(null);
  const [selectedModelId, setSelectedModelId] = useState('anthropic.claude-3-5-sonnet-20241022-v2:0');
  const [parsing, setParsing] = useState(false);
  const [detectedChanges, setDetectedChanges] = useState<DetectedChange[]>([]);
  const [impactSummary, setImpactSummary] = useState<ImpactSummary | null>(null);
  const [changeRequestId, setChangeRequestId] = useState<number | null>(null);
  const [approvedChanges, setApprovedChanges] = useState<Set<number>>(new Set());
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (currentProject?.id) {
      loadProcessMaps();
    }
  }, [currentProject]);

  const loadProcessMaps = async () => {
    try {
      const token = localStorage.getItem('jarvis_access_token');
      const response = await api.get(`/api/process-maps/project/${currentProject!.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const maps = response.data.process_maps || [];
      setProcessMaps(maps);
      if (maps.length > 0) {
        setSelectedMapId(maps[0].id);
      }
    } catch (error) {
      console.error('Failed to load process maps:', error);
      toast.error('Failed to load process maps');
    }
  };

  const handleParseChanges = async () => {
    if (!selectedMapId || !sourceContent) {
      toast.error('Please select a process map and enter content to analyze');
      return;
    }

    setParsing(true);
    try {
      const token = localStorage.getItem('jarvis_access_token');
      const response = await api.post('/api/change-detection/parse', {
        project_id: currentProject!.id,
        process_map_id: selectedMapId,
        source_content: sourceContent,
        source_type: sourceType,
        source_reference: sourceReference,
        model_id: selectedModelId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setDetectedChanges(response.data.detected_changes);
      setImpactSummary(response.data.impact_summary);
      setChangeRequestId(response.data.change_request_id);

      const allApproved = new Set<number>(response.data.detected_changes.map((c: DetectedChange) => c.id));
      setApprovedChanges(allApproved);

      toast.success(`Detected ${response.data.detected_changes.length} changes`);
    } catch (error: any) {
      console.error('Parse error:', error);
      toast.error(error.response?.data?.message || 'Failed to parse changes');
    } finally {
      setParsing(false);
    }
  };

  const toggleApproval = (changeId: number) => {
    const newApproved = new Set(approvedChanges);
    if (newApproved.has(changeId)) {
      newApproved.delete(changeId);
    } else {
      newApproved.add(changeId);
    }
    setApprovedChanges(newApproved);
  };

  const handleApplyChanges = async () => {
    if (!changeRequestId) {
      toast.error('No change request to apply');
      return;
    }

    if (approvedChanges.size === 0) {
      toast.error('No changes approved');
      return;
    }

    setApplying(true);
    try {
      const token = localStorage.getItem('jarvis_access_token');
      const response = await api.post(`/api/change-detection/${changeRequestId}/apply`, {
        approved_change_ids: Array.from(approvedChanges)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Applied ${response.data.applied_count} changes successfully!`);
      
      setDetectedChanges([]);
      setImpactSummary(null);
      setSourceContent('');
      setApprovedChanges(new Set());
      
      await loadProcessMaps();
    } catch (error: any) {
      console.error('Apply error:', error);
      toast.error(error.response?.data?.message || 'Failed to apply changes');
    } finally {
      setApplying(false);
    }
  };

  const getChangeTypeColor = (type: string) => {
    const colors = {
      ADD: 'bg-green-100 text-green-800 border-green-300',
      MODIFY: 'bg-blue-100 text-blue-800 border-blue-300',
      DELETE: 'bg-red-100 text-red-800 border-red-300',
      MERGE: 'bg-purple-100 text-purple-800 border-purple-300',
      SPLIT: 'bg-orange-100 text-orange-800 border-orange-300',
      REORDER: 'bg-yellow-100 text-yellow-800 border-yellow-300'
    };
    return colors[type as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-super-dark-purple">Change Detection & Approval</h1>
          <p className="text-gray-600">Intelligently detect and apply incremental updates to process maps</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Process Map</label>
            <select
              value={selectedMapId || ''}
              onChange={(e) => setSelectedMapId(Number(e.target.value))}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              {processMaps.map(map => (
                <option key={map.id} value={map.id}>
                  Level {map.level} - {map.process_name || map.title}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Source Type</label>
            <div className="flex space-x-2">
              {(['notes', 'transcript', 'document', 'manual'] as const).map(type => (
                <button
                  key={type}
                  onClick={() => setSourceType(type)}
                  className={`px-4 py-2 rounded-md ${
                    sourceType === type 
                      ? 'bg-electric-blue text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Reference (Optional)</label>
          <input
            type="text"
            value={sourceReference}
            onChange={(e) => setSourceReference(e.target.value)}
            placeholder="e.g., Q4 Planning Meeting - Oct 7, 2025"
            className="w-full border border-gray-300 rounded-md px-3 py-2"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Content to Analyze</label>
          <textarea
            value={sourceContent}
            onChange={(e) => setSourceContent(e.target.value)}
            rows={12}
            placeholder="Paste meeting notes, transcript, or document content containing process changes..."
            className="w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-sm"
          />
        </div>

        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-600">
            {sourceContent.length} characters
          </div>
          <button
            onClick={handleParseChanges}
            disabled={parsing || !selectedMapId || !sourceContent}
            className="bg-electric-blue text-white px-6 py-2 rounded-md hover:bg-darker-blue disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {parsing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Parsing...</span>
              </>
            ) : (
              <>
                <PlayCircle className="w-4 h-4" />
                <span>Parse Changes</span>
              </>
            )}
          </button>
        </div>
      </div>

      {impactSummary && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">Impact Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-2xl font-bold text-blue-600">{impactSummary.total_changes}</div>
              <div className="text-sm text-gray-600">Total Changes</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">{impactSummary.add_count}</div>
              <div className="text-sm text-gray-600">Additions</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-600">{impactSummary.modify_count}</div>
              <div className="text-sm text-gray-600">Modifications</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-red-600">{impactSummary.delete_count}</div>
              <div className="text-sm text-gray-600">Deletions</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-600">{impactSummary.overall_confidence}%</div>
              <div className="text-sm text-gray-600">Avg Confidence</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-orange-600">{impactSummary.affected_steps}</div>
              <div className="text-sm text-gray-600">Affected Steps</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-600">{impactSummary.preserved_steps}</div>
              <div className="text-sm text-gray-600">Preserved Steps</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">{impactSummary.high_confidence_changes}</div>
              <div className="text-sm text-gray-600">High Confidence (&gt;85%)</div>
            </div>
          </div>
        </div>
      )}

      {detectedChanges.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Detected Changes</h3>
            <div className="text-sm text-gray-600">
              {approvedChanges.size} of {detectedChanges.length} approved
            </div>
          </div>

          <div className="space-y-4">
            {detectedChanges.map((change, index) => (
              <div
                key={change.id}
                className={`border-2 rounded-lg p-4 ${
                  approvedChanges.has(change.id) ? 'border-green-300 bg-green-50' : 'border-gray-200'
                }`}
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center space-x-3">
                    <div className={`px-3 py-1 rounded-full text-xs font-semibold border ${getChangeTypeColor(change.change_type)}`}>
                      {change.change_type}
                    </div>
                    <div className="text-sm font-medium text-gray-700">
                      Change #{index + 1}
                    </div>
                    {change.confidence_score >= 85 && (
                      <div className="flex items-center space-x-1 text-green-600 text-xs">
                        <CheckCircle2 className="w-3 h-3" />
                        <span>High Confidence</span>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => toggleApproval(change.id)}
                    className={`px-4 py-1 rounded-md text-sm font-medium ${
                      approvedChanges.has(change.id)
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {approvedChanges.has(change.id) ? 'Approved' : 'Approve'}
                  </button>
                </div>

                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium">Target:</span> {change.target_location}
                    {change.section_name && (
                      <span className="text-gray-600"> ({change.section_name})</span>
                    )}
                  </div>
                  
                  <div>
                    <span className="font-medium">Confidence:</span> {change.confidence_score}%
                  </div>

                  {change.current_content && (
                    <div className="bg-red-50 border border-red-200 rounded p-2">
                      <span className="font-medium text-red-800">Current:</span>
                      <p className="text-gray-700 mt-1">{change.current_content}</p>
                    </div>
                  )}

                  <div className="bg-green-50 border border-green-200 rounded p-2">
                    <span className="font-medium text-green-800">Proposed:</span>
                    <p className="text-gray-700 mt-1">{change.proposed_content}</p>
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded p-2">
                    <span className="font-medium">Reasoning:</span>
                    <p className="text-gray-700 mt-1">{change.reasoning}</p>
                  </div>

                  {change.requires_override && (
                    <div className="flex items-center space-x-2 text-amber-600 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      <span>Requires override (locked/verified step)</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              onClick={() => {
                setDetectedChanges([]);
                setImpactSummary(null);
                setApprovedChanges(new Set());
              }}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleApplyChanges}
              disabled={applying || approvedChanges.size === 0}
              className="bg-electric-blue text-white px-6 py-2 rounded-md hover:bg-darker-blue disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {applying ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Applying...</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Apply {approvedChanges.size} Approved Changes</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangeDetection;
